// Phase D1's town stream (`?scene=town`): the townsfolk and their routines (src/town/module.ts, on @rpg/ai's
// routines), the cutscenes on the shot system (cutscene.ts, on @rpg/camera's timelines, gate D1.4), the crowd's
// animation LOD (view.ts, on @rpg/figure's AnimationLod, gate D1.5) and the town's facts on the one condition language
// (facts.ts; the story asks for the scenes and talks on `npc:interact` since phase D joined). The handle's keys
// are under `__rpg.town`; the planted defects join the spike's faults record through `townFaults`; the D1.5 perf tour
// through the town and the evidence framings are here.
import * as THREE from "three";
import { LOD_FAULTS } from "@rpg/figure";
import { blockAt } from "@rpg/ai";
import { TIMELINE_FAULTS, cameraAt, type Placed } from "@rpg/camera";
import { hashState, hashWorld, type EventBus, type Game } from "@rpg/kernel";
import { overlay, perfReport, runTour, type TourSegment } from "@rpg/perf";
import type { Invariant } from "@rpg/harness";
import type { LowPolyLook } from "@rpg/look";
import type { Runtime } from "@rpg/runtime";
import type { GameEvents } from "../core";
import type { FaultGroup } from "../faults";
import type { SpikeContent } from "../content";
import { SKY_ACTIONS } from "../sky/module";
import { teleportHero } from "../world/teleport";
import type { WorldMount } from "../world";
import { CUTSCENE_ACTIONS, CUTSCENE_C } from "./cutscene";
import { TOWN_FAULTS } from "./module";
import { townScene, type TownMount } from "./scene";
import { TOWN_VIEW_FAULTS, mountTownView, type TownView } from "./view";

export { townScene, type TownMount } from "./scene";

/** The town's planted defects, as `?faults=` and `__rpg.faults()` take them. */
export const TOWN_FAULT_NAMES = ["routineHourIgnored", "offscreenPosedEveryTick", "cutsceneSkipDropsState", "cutsceneWallClock", "cutscenePauseIgnored"] as const;
type TownFaultName = (typeof TOWN_FAULT_NAMES)[number];

/** The kernel game whose pause the `cutscenePauseIgnored` defect breaks (set when the town mounts). */
let gameRef: Game<GameEvents> | null = null;
let pauseIgnored = false;

/** The group for src/faults.ts's joined record (each switch lives beside what it breaks). */
export const townFaults: FaultGroup = {
  names: TOWN_FAULT_NAMES,
  get: (n) => {
    switch (n as TownFaultName) {
      case "routineHourIgnored":
        return TOWN_FAULTS.routineHourIgnored;
      case "offscreenPosedEveryTick":
        return TOWN_VIEW_FAULTS.offscreenPosedEveryTick;
      case "cutsceneSkipDropsState":
        return TIMELINE_FAULTS.skipDropsState;
      case "cutsceneWallClock":
        return TIMELINE_FAULTS.wallClock;
      case "cutscenePauseIgnored":
        return pauseIgnored;
      default:
        return false;
    }
  },
  set: (n, on) => {
    switch (n as TownFaultName) {
      case "routineHourIgnored":
        TOWN_FAULTS.routineHourIgnored = on;
        break;
      case "offscreenPosedEveryTick":
        TOWN_VIEW_FAULTS.offscreenPosedEveryTick = on;
        LOD_FAULTS.offscreenEveryTick = on;
        break;
      case "cutsceneSkipDropsState":
        TIMELINE_FAULTS.skipDropsState = on;
        break;
      case "cutsceneWallClock":
        TIMELINE_FAULTS.wallClock = on;
        break;
      case "cutscenePauseIgnored":
        pauseIgnored = on;
        if (gameRef) gameRef.faults.pauseIgnored = on;
        break;
    }
  },
};

/** Turn on the town's defects `?faults=` names. */
export function applyTownFaultSwitches(search: string): void {
  const on = (new URLSearchParams(search).get("faults") ?? "").split(",");
  for (const n of TOWN_FAULT_NAMES) if (on.includes(n)) townFaults.set(n, true);
}

/** Wrap the world mount with the town (`?scene=town`). */
export function townSceneFor(m: WorldMount, o: { content: SpikeContent; runtime: Runtime; params: URLSearchParams }): TownMount {
  applyTownFaultSwitches(`?${o.params.toString()}`);
  return townScene(m, o);
}

/** Where the evidence shots and the tour stand the hero (x, z, yaw; the follow camera frames from behind) and the
 *  hour (agent framings). */
const VIEWS: Record<string, { at: [number, number, number] | { npc: string; dist: number; angle: number }; hours: number }> = {
  // on the road where it enters the square from the west, looking across it to the well and the notice board
  "square-day": { at: [54, -25, 1.93], hours: 12.5 },
  "square-evening": { at: [54, -25, 1.93], hours: 19.2 },
  // in front of the smith, a little to one side, so the hammer shows
  forge: { at: { npc: "smith", dist: 3.4, angle: 0.75 }, hours: 10 },
};

/** A town event the handle logs. */
export interface TownLogEntry {
  step: number;
  type: keyof GameEvents;
  data: unknown;
}

export function mountTown(o: {
  town: TownMount;
  runtime: Runtime;
  game: Game<GameEvents>;
  events: EventBus<GameEvents>;
  /** The scene's hashed component types (the world's state hash, without the cutscenes' own log). */
  hashed: readonly string[];
  physicsHash: () => string;
  rngHash: () => string;
  dispatch: (action: string, payload: unknown) => void;
  tier: string;
  /** Build every chunk the view tier wants now (blocking): a frame then shows the same world whatever the streamer's
   *  per-frame budget managed before (the same-frames check). */
  settleView?: () => void;
}) {
  const { town, runtime } = o;
  gameRef = o.game;
  o.game.faults.pauseIgnored = pauseIgnored;
  const m = town.world;
  let view: TownView | null = null;
  let renderer: THREE.WebGLRenderer | null = null;
  let scene: THREE.Scene | null = null;
  let look: LowPolyLook | null = null;
  const log: TownLogEntry[] = [];
  for (const type of ["npc:interact", "dialogue:open", "dialogue:line", "dialogue:close", "story:scene", "story:scene-done"] as const)
    o.events.on(type, (data: unknown) => {
      log.push({ step: runtime.clock.step, type, data: structuredClone(data) });
      if (log.length > 400) log.splice(0, 100);
    });
  const place = (x: number, z: number, yaw: number): void => {
    const r = m.rig();
    teleportHero(m, x, z, yaw);
    r.yawHeld = true;
    r.camera?.setYaw(yaw);
    r.control().cameraYaw = yaw;
  };
  const npcs = () =>
    town
      .folk()
      .state()
      .npcs.map((n) => ({
        id: n.id,
        x: n.pos[0],
        y: n.pos[1],
        z: n.pos[2],
        yaw: n.yaw,
        mode: n.mode,
        place: town.facts.get(`npc.${n.id}.place`) ?? null,
        block: n.routine.block,
        routinePlace: n.routine.place,
        spot: n.routine.spot,
        arrived: n.routine.arrived,
        activity: n.activity,
        talking: n.talk !== null,
      }));
  /** Where an NPC's spot for this hour is (its routine block's place and spot). */
  const spotOf = (id: string): { place: string; x: number; z: number; yaw: number | null } | null => {
    const n = town.folk().npc(id);
    if (!n.routine.place) return null;
    const p = town.places.place(n.routine.place.startsWith("home:") ? "home" : n.routine.place, id);
    const s = p?.spots[n.routine.spot];
    return s ? { place: p!.id, x: s.x, z: s.z, yaw: s.yaw ?? null } : null;
  };
  /** The world's state hash without the cutscenes' own log (what a scene leaves the world as). */
  const worldHash = (): string => hashState({ kernel: hashWorld(o.game.world, o.hashed.filter((c) => c !== CUTSCENE_C)), physics: o.physicsHash(), rng: o.rngHash() });
  const setView = (name: string): void => {
    const v = VIEWS[name];
    if (!v) throw new Error(`town: no view "${name}" (have ${Object.keys(VIEWS).join(", ")})`);
    o.dispatch(SKY_ACTIONS.set, { hours: v.hours });
    o.dispatch(SKY_ACTIONS.weather, { id: "clear", instant: true });
    o.dispatch(SKY_ACTIONS.wet, { wet: 0 });
    m.rig().actions.setStick("move", null);
    if (Array.isArray(v.at)) return place(...v.at);
    // one step so the townsfolk snap to the hour, then the hero in front of the NPC, facing it
    runtime.advance(1000 / 120, { draw: false });
    const n = town.folk().npc(v.at.npc);
    const a = n.yaw + v.at.angle;
    const x = n.pos[0] + Math.sin(a) * v.at.dist;
    const z = n.pos[2] + Math.cos(a) * v.at.dist;
    // facing a little past the NPC, so the follow camera behind the hero frames it beside the hero, not behind it
    place(x, z, Math.atan2(n.pos[0] - x, n.pos[2] - z) + 0.35);
  };
  const frames = (ms: number, step = 50): void => {
    for (let t = 0; t < ms; t += step) runtime.advance(step, { draw: true });
  };

  /** The D1.5 perf tour: through the town by day, the evening square, then looking away from it; per segment the LOD's
   *  counters (posed against seen, by band and off screen). */
  const perSegment: Record<string, ReturnType<TownView["report"]>["lod"] & { poses: ReturnType<TownView["report"]>["poses"] }> = {};
  const tour = (ffMs = 100): void => {
    if (!renderer || !scene || !look || !view) throw new Error("town tour: no view");
    const v = view;
    const lk = look;
    const ui = overlay(o.tier === "low" ? "low" : "high");
    document.getElementById("perf-tour")?.setAttribute("data-no-strings", "");
    const t0 = { s: 0 };
    const segs: TourSegment[] = [
      { name: "warmup", start: () => setView("square-day"), tick: (t) => t > 0.8, max: 4 },
      {
        name: "town-day",
        // the hero runs through the town along the road, the townsfolk at their day's places
        start: () => {
          setView("square-day");
          place(50, -21.5, 2.21);
          m.rig().actions.setStick("move", { x: 0, y: 1 });
        },
        tick: (t) => t > 8,
        max: 12,
      },
      {
        name: "square-evening",
        // everyone gathered in the square, the camera on it
        start: () => {
          m.rig().actions.setStick("move", null);
          setView("square-evening");
        },
        tick: (t) => t > 5,
        max: 10,
      },
      {
        name: "look-away",
        // the square behind the camera: its figures off screen
        start: () => place(54, -25, 1.93 + Math.PI),
        tick: (t) => t > 3,
        max: 6,
      },
    ];
    for (const sg of segs) {
      const st = sg.start;
      sg.start = () => {
        t0.s = runtime.clock.time;
        v.resetCounters();
        st?.();
      };
      const tick = sg.tick;
      sg.tick = (t) => {
        const done = tick(t - t0.s);
        const r = v.report();
        perSegment[sg.name] = { ...r.lod, poses: r.poses };
        return done;
      };
    }
    runTour({
      kind: "town-crowd",
      renderer,
      scene,
      composer: lk.composer,
      game: { simT: () => runtime.clock.time, pause: (on) => void (runtime.paused = on), advanceTime: (ms, draw) => void runtime.advance(ms, { draw: draw ?? true }) },
      segments: segs,
      ffMs,
      route: "town",
      estSeconds: 20,
      tier: o.tier === "low" ? "low" : "high",
      ui,
      readyAt: performance.now(),
      quality: () => ({ ...lk.report() }),
      extra: () => ({ town: { npcs: town.content.npcs.length, lod: structuredClone(perSegment) } }),
      onEnd: () => {
        m.rig().actions.setStick("move", null);
        m.rig().yawHeld = false;
      },
    });
  };

  const handle = {
    /** Every NPC: where it is, its mode, the place fact, its routine's block and spot, its activity. */
    npcs,
    npc: (id: string) => npcs().find((n) => n.id === id) ?? null,
    /** An NPC's spot for its block now. */
    spotOf,
    /** The facts the town registers (the stub registry story replaces): every one, or one by key. */
    facts: () => town.facts.all(),
    fact: (key: string) => town.facts.get(key) ?? null,
    /** A routine's block at an hour, read from the routine table itself (the planted defect breaks the schedule the
     *  townsfolk follow, not this). */
    blockAt: (id: string, hour: number) => blockAt(town.folk().def(id).routine, hour),
    /** The clock: set the hour (forward), the day's pace. */
    setHour: (hours: number) => o.dispatch(SKY_ACTIONS.set, { hours }),
    rate: (rate: number) => o.dispatch(SKY_ACTIONS.rate, { rate }),
    hour: () => m.sky().hours(),
    /** Put the hero at (x, z) facing `yaw` (its chunks loaded, the camera's yaw held). */
    place,
    /** Put the hero `dist` m from an NPC, facing it: in front of it, or along `bearing` (a world yaw from the NPC). */
    approach(id: string, dist = 1.3, bearing?: number) {
      const n = town.folk().npc(id);
      const yaw = bearing ?? n.yaw;
      const x = n.pos[0] + Math.sin(yaw) * dist;
      const z = n.pos[2] + Math.cos(yaw) * dist;
      place(x, z, yaw + Math.PI);
      return { x, z };
    },
    /** Press interact on the next step (through phase C's interaction system). */
    press: () => void (m.things().press = true),
    /** What the interaction chooser picked last. */
    choice: () => structuredClone(m.things().choice()),
    /** The town events since boot (npc:interact, dialogue:*, story:scene*), newest last. */
    log: (n = 50) => log.slice(-n),
    /** The townsfolk's own counters. */
    stats: () => ({ talks: town.folk().state().talks, snaps: town.folk().state().snaps, crowd: town.folk().crowd.stats(), live: town.folk().state().npcs.filter((n) => n.mode === "live").length, steps: town.folk().steps() }),
    /** The world's state hash without the cutscenes' log (the skip check compares it). */
    worldHash,
    /** The cutscenes: play one, skip the one playing, their state and flags; the frame the view drew; a timeline's
     *  marks (its steps' starts and its end); the camera a timeline gives at a time, sampled in the sim's state now. */
    cutscene: {
      play: (scene: string) => o.dispatch(CUTSCENE_ACTIONS.play, { scene }),
      skip: () => o.dispatch(CUTSCENE_ACTIONS.skip, {}),
      state: () => structuredClone(town.cutscenes().state()),
      active: () => town.cutscenes().active(),
      paused: () => [...o.game.paused()],
      flags: () => ({ ...town.cutscenes().state().flags }),
      scenes: () => [...town.content.scenes.values()].map((s) => ({ id: s.id, duration: s.timeline.duration, marks: s.timeline.marks, actors: s.timeline.actors, puppets: s.puppets.map((p) => p.actor) })),
      frame: () => view?.report().cutscene ?? null,
      cameraAt: (scene: string, t: number) => {
        const d = town.content.scenes.get(scene);
        const p = town.cutscenes().state().playing;
        if (!d) throw new Error(`town: no scene "${scene}"`);
        const starts: Record<string, Placed> = p && p.scene === scene ? p.starts : {};
        return cameraAt(d.timeline, t, starts, town.cutscenes().resolver);
      },
    },
    /** Build every chunk the view tier wants now (a frame then shows the same world whatever the streamer's budget did). */
    settleView: () => o.settleView?.(),
    /** The view: the figures, the LOD's counters, the pose counter, the cutscene frame. */
    view: () => view?.report() ?? null,
    resetCounters: () => view?.resetCounters(),
    /** Evidence framings: the hour set, the hero placed; `shot` also plays `ms` of drawn frames. */
    views: () => Object.keys(VIEWS),
    setView,
    shot: (name: string, ms = 1500) => {
      setView(name);
      frames(ms);
    },
    /** Gate D1.5: the perf tour through the town (read `perf()`); the LOD's counters by segment. */
    tour,
    perf: () => perfReport(),
    tourLod: () => structuredClone(perSegment),
  };

  return {
    handle,
    /** Mount the view on the valley's look (inside its warm-up, so its figures' programs compile before ready). */
    mountView(v: { scene: THREE.Scene; camera: THREE.PerspectiveCamera; look: LowPolyLook; content: SpikeContent; renderer: THREE.WebGLRenderer; snapCamera?: () => void }): { view: TownView; clearWarm: () => void } {
      scene = v.scene;
      renderer = v.renderer;
      look = v.look;
      view = mountTownView({ runtime, scene: v.scene, camera: v.camera, look: v.look, content: v.content, town, tierCasts: v.look.report().shadowMapSize > 0, ...(v.snapCamera ? { snapCamera: v.snapCamera } : {}) });
      const h = m.rig().hero();
      const clearWarm = view.warm({ x: h.feet.x, y: h.feet.y, z: h.feet.z });
      // the sun's shadow camera on them too, so the skinned depth program compiles in the warm-up's shadow pass
      v.look.focus(new THREE.Vector3(h.feet.x, h.feet.y, h.feet.z));
      return { view, clearWarm };
    },
    invariants: (): Invariant[] => [
      () => {
        for (const n of town.folk().state().npcs) {
          if (!n.pos.every(Number.isFinite)) return `town: ${n.id} at a non-finite place`;
          if (n.mode !== "inside" && n.pos[1] < m.world.heightAt(n.pos[0], n.pos[2]) - 0.6) return `town: ${n.id} below the ground`;
        }
        return null;
      },
      () => (o.game.paused().includes("cutscene") !== town.cutscenes().active() ? "cutscene: the world's pause is out of step with the scene" : null),
    ],
    state: () => ({ npcs: town.content.npcs.length, live: town.folk().state().npcs.filter((n) => n.mode === "live").length, inside: town.folk().state().npcs.filter((n) => n.mode === "inside").length, cutscene: town.cutscenes().state().playing?.scene ?? null }),
    view: () => view,
  };
}
